import axios from 'axios';

/**
 * Retry for reads whose failure would otherwise leave a screen silently wrong.
 *
 * Introduced by the 2026-09-11 silent-catch review. Several components loaded
 * something once, and a single network blip left them in a state the user could
 * not distinguish from a real answer: a suspension banner that never appeared, a
 * compliance banner that hid an expired ID, a live-update stream that never
 * opened. Retrying transient failures fixes the common case without inventing a
 * UI for each one.
 *
 * Deliberately narrow: only failures that a second attempt can change are
 * retried. A 4xx is the server's answer, not an accident, and retrying a 403 or
 * a 404 only delays the same response.
 */

/** A failure worth trying again: no response, a timeout, a 5xx, or a 429. */
export function isTransientError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  if (!err.response) return true;
  const status = err.response.status;
  return status >= 500 || status === 429;
}

export interface RetryOptions {
  /** Total attempts including the first. Default 3. */
  readonly attempts?: number;
  /** Delay before the first retry; doubles on each subsequent one. Default 1000 ms. */
  readonly baseDelayMs?: number;
  /** Stop as soon as this returns true — typically an unmounted component. */
  readonly isCancelled?: () => boolean;
  /** Which failures to retry. Default `isTransientError`. */
  readonly shouldRetry?: (err: unknown) => boolean;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

/** Run `fn`, retrying transient failures with exponential backoff. Rethrows the
 *  last error when attempts run out, the error is not retryable, or the caller
 *  has been cancelled. */
export async function withRetry<T>(fn: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
  const attempts = Math.max(1, opts.attempts ?? 3);
  const baseDelayMs = opts.baseDelayMs ?? 1000;
  const shouldRetry = opts.shouldRetry ?? isTransientError;

  for (let attempt = 1; ; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      const exhausted = attempt >= attempts;
      if (exhausted || !shouldRetry(err) || opts.isCancelled?.()) throw err;
      await sleep(baseDelayMs * 2 ** (attempt - 1));
      if (opts.isCancelled?.()) throw err;
    }
  }
}
